import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type {
  ApDocumentType,
  ApEmailResponse,
  ApInvoice,
  ApReceipt,
  ApVendor,
} from "@/accounts-payable/types";
import { enrichApEmail } from "@/accounts-payable/utils";
import { useApi } from "@/core/api";
import { documentKey } from "./document-key";
import {
  AP_DOCUMENT_ENDPOINTS,
  emailKey,
  invoiceDocumentKey,
  invoiceKey,
  receiptKey,
  vendorKey,
} from "./keys";
import { AP_STALE_TIME } from "./polling";

export const useGetReceipt = (
  businessId?: string | null,
  receiptId?: string | null,
) => {
  const api = useApi();
  return useQuery({
    queryKey: receiptKey(businessId, receiptId),
    queryFn: () =>
      api
        .get<ApReceipt>(`/businesses/${businessId}/ap/receipts/${receiptId}`)
        .then((response) => response.data),
    enabled: !!businessId && !!receiptId,
    staleTime: AP_STALE_TIME,
  });
};

export const useGetPurchaseDocument = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
  documentId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const api = useApi();
  return useQuery({
    queryKey: documentKey(documentType, businessId, documentId),
    queryFn: () =>
      api
        .get<
          ApInvoice | ApReceipt
        >(`/businesses/${businessId}/ap/${AP_DOCUMENT_ENDPOINTS[documentType].path}/${documentId}`)
        .then((response) => response.data),
    enabled: enabled && !!businessId && !!documentId,
    staleTime: AP_STALE_TIME,
  });
};

export const invoiceQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  invoiceId: string | null | undefined,
) =>
  queryOptions({
    queryKey: invoiceKey(businessId, invoiceId),
    queryFn: () =>
      api
        .get<ApInvoice>(`/businesses/${businessId}/ap/invoices/${invoiceId}`)
        .then((response) => response.data),
    enabled: !!businessId && !!invoiceId,
    staleTime: AP_STALE_TIME,
  });

export const useGetInvoice = (
  businessId?: string | null,
  invoiceId?: string | null,
) => {
  const api = useApi();
  return useQuery(invoiceQueryOptions(api, businessId, invoiceId));
};

export const useGetInvoicesByIds = (
  businessId: string | null | undefined,
  invoiceIds: string[],
) => {
  const api = useApi();
  return useQueries({
    queries: invoiceIds.map((invoiceId) =>
      invoiceQueryOptions(api, businessId, invoiceId),
    ),
    combine: (results) => ({
      invoices: results.flatMap((result) => (result.data ? [result.data] : [])),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    }),
  });
};

export const emailQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  emailId: string | null | undefined,
) =>
  queryOptions({
    queryKey: emailKey(businessId, emailId),
    queryFn: () =>
      api
        .get<ApEmailResponse>(`/businesses/${businessId}/ap/emails/${emailId}`)
        .then((response) => enrichApEmail(response.data)),
    enabled: !!businessId && !!emailId,
    staleTime: AP_STALE_TIME,
  });

export const useGetEmail = (
  businessId?: string | null,
  emailId?: string | null,
) => {
  const api = useApi();
  return useQuery(emailQueryOptions(api, businessId, emailId));
};

export const vendorQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  vendorId: string | null | undefined,
) =>
  queryOptions({
    queryKey: vendorKey(businessId, vendorId),
    queryFn: () =>
      api
        .get<ApVendor>(`/businesses/${businessId}/ap/vendors/${vendorId}`)
        .then((response) => response.data),
    enabled: !!businessId && !!vendorId,
    staleTime: AP_STALE_TIME,
  });

export const useGetVendor = (
  businessId?: string | null,
  vendorId?: string | null,
) => {
  const api = useApi();
  return useQuery(vendorQueryOptions(api, businessId, vendorId));
};

export const invoiceDocumentQueryOptions = (
  api: AxiosInstance,
  documentSrc: string | null | undefined,
) =>
  queryOptions({
    queryKey: invoiceDocumentKey(documentSrc),
    queryFn: () =>
      api
        .get<Blob>(documentSrc as string, { responseType: "blob" })
        .then((response) => response.data),
    enabled: !!documentSrc,
    staleTime: 5 * 60 * 1000,
  });
